import ErrorRounded from "@mui/icons-material/ErrorRounded";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import Typography from "@mui/material/Typography";
import isNil from "lodash/isNil";
import PropTypes from "prop-types";
import { useEffect, useState, lazy, Suspense } from "react";
import { useLoanFilesHook } from "../../hooks/useLoanFilesHook";

// Lazy load components
const DownloadLoanFileIconButton = lazy(
  () =>
    import(
      "../../screens/LoanDrilldownScreen/shared/DownloadLoanFileIconButton"
    )
);
const FilePreviewImageTypeIcon = lazy(
  () => import("./FilePreviewImageTypeIcon")
);

// Loading fallback component
const LoadingFallback = () => (
  <Box
    sx={{
      display: "flex",
      justifyContent: "center",
      alignItems: "center",
      height: 50,
    }}
  >
    <CircularProgress size={20} />
  </Box>
);

const FilePreview = ({
  fileObject,
  showDownloadButton = true,
  height = "100%",
  width = "100%",
  sx = {},
  // optional overrides for extracted fields:
  contentType: overrideContentType,
  fileDisplayName: overrideDisplayName,
  fileStorageName: overrideStorageName,
  loanId: overrideLoanId,
}) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const { getFileById } = useLoanFilesHook();

  // derive or override
  const contentType = overrideContentType || fileObject?.content_type;
  const fileDisplayName = overrideDisplayName || fileObject?.file_display_name;
  const fileStorageName = overrideStorageName || fileObject?.file_storage_name;
  const loanId =
    overrideLoanId || fileObject?.dealId || fileObject?.containerName;

  useEffect(
    () => () => {
      if (previewUrl) URL.revokeObjectURL(previewUrl);
    },
    [previewUrl]
  );

  useEffect(() => {
    if (fileObject && fileStorageName && loanId) {
      setLoading(true);
      setError(null);
      getFileById({
        loanId,
        file_storage_name: fileStorageName,
        isFile: true,
        onSuccessFn: (data) => {
          try {
            const blob =
              data instanceof Blob
                ? data
                : new Blob([data], { type: contentType });
            setPreviewUrl(URL.createObjectURL(blob));
          } catch (e) {
            console.error("Blob URL error:", e);
            setError("Unable to process file preview.");
          } finally {
            setLoading(false);
          }
        },
        onErrorFn: (err) => {
          console.error("Fetch error:", err);
          setError("Failed to load file.");
          setLoading(false);
        },
      });
    }
  }, [fileObject, fileStorageName, loanId, contentType, getFileById]);

  if (isNil(fileObject)) return null;

  const isImage = contentType?.startsWith("image/");
  const isPdf = contentType === "application/pdf";

  const handleRetry = () => {
    setError(null);
    setLoading(true);
  };

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        height,
        width,
        background: "#fff",
        position: "relative",
        overflow: "hidden",
        ...sx,
      }}
    >
      <Box
        sx={{
          position: "relative",
          flexGrow: 1,
          bgcolor: (theme) => theme.palette.grey[50],
        }}
      >
        {loading && (
          <Box
            sx={{
              position: "absolute",
              inset: 0,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              background: "rgba(0,0,0,0.6)",
            }}
          >
            <CircularProgress sx={{ color: "#fff" }} />
          </Box>
        )}
        {error && (
          <Box
            sx={{
              position: "absolute",
              inset: 0,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              backgroundColor: "rgba(255,255,255,0.8)",
              p: 2,
            }}
          >
            <Box sx={{ textAlign: "center" }}>
              <ErrorRounded color="error" sx={{ fontSize: 40 }} />
              <Typography color="error" sx={{ mt: 1 }}>
                {error}
              </Typography>
              <Button onClick={handleRetry} sx={{ mt: 1 }}>
                Try Again
              </Button>
            </Box>
          </Box>
        )}
        {!loading && !error && previewUrl && (
          <>
            {isImage && (
              <Box
                sx={{
                  p: 2,
                  display: "flex",
                  justifyContent: "center",
                  height: "100%",
                  background: "#282828",
                }}
              >
                <img
                  src={previewUrl}
                  alt={fileDisplayName}
                  style={{
                    maxWidth: "100%",
                    maxHeight: "100%",
                    objectFit: "contain",
                    border: "1px solid #fff8",
                    borderRadius: 2,
                  }}
                />
              </Box>
            )}
            {isPdf && (
              <iframe
                src={previewUrl}
                title={fileDisplayName}
                style={{ width: "100%", height: "100%", border: "none" }}
              />
            )}
            {!isImage && !isPdf && (
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  height: "100%",
                  p: 3,
                }}
              >
                <Suspense fallback={<LoadingFallback />}>
                  <FilePreviewImageTypeIcon contentType={contentType} />
                </Suspense>
                <Typography variant="body1" sx={{ mt: 2, textAlign: "center" }}>
                  Cannot preview this file type.
                </Typography>
                {showDownloadButton && (
                  <Suspense fallback={<LoadingFallback />}>
                    <DownloadLoanFileIconButton
                      loanId={loanId}
                      fileObject={fileObject}
                    />
                  </Suspense>
                )}
              </Box>
            )}
          </>
        )}
      </Box>
    </Box>
  );
};

FilePreview.propTypes = {
  fileObject: PropTypes.object.isRequired,
  showDownloadButton: PropTypes.bool,
  height: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  width: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  sx: PropTypes.object,
  contentType: PropTypes.string,
  fileDisplayName: PropTypes.string,
  fileStorageName: PropTypes.string,
  loanId: PropTypes.string,
};

export default FilePreview;
